"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { motion, type Variants } from "framer-motion";

export default function About() {
  const { t } = useTranslation();

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 18 },
    },
  };

  return (
    <section className="bg-white pb-20 md:pb-28">
      <motion.div
        className="mx-auto w-[92%] max-w-[1800px] px-5 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        {/* Heading + intro */}
        <div className="grid items-start gap-6 lg:grid-cols-12 lg:gap-12">
          <motion.h2
            variants={itemVariants}
            className="text-h2 text-slate-900 leading-[1.15] tracking-tight md:text-[2.4rem] lg:col-span-7"
          >
            {/* "|" in the translation marks the desktop-only line break */}
            {t("about.title").split("|")[0]}{" "}
            <br className="hidden md:block" />
            {t("about.title").split("|")[1]}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-[17px] leading-[28px] text-slate-600 lg:col-span-5"
          >
            {t("about.desc")}
          </motion.p>
        </div>

        {/* Stats: big gradient numbers, hairline dividers, no cards */}
        <div className="mt-14 grid grid-cols-2 border-t border-slate-200 lg:mt-20 lg:grid-cols-4">
          {[1, 2, 3, 4].map((n) => (
            <motion.div
              key={n}
              variants={itemVariants}
              className="border-b border-slate-200 py-8 pr-6 odd:border-r max-lg:odd:pr-6 max-lg:even:pl-6 lg:border-b-0 lg:border-r lg:px-8 lg:py-10 lg:first:pl-0 lg:last:border-r-0 lg:last:pr-0"
            >
              <p className="bg-gradient-to-r from-[#056F90] to-[#0EA5E9] bg-clip-text text-2xl font-extrabold leading-none tracking-tight text-transparent min-[400px]:text-3xl sm:text-4xl xl:text-5xl 2xl:text-6xl">
                {t(`about.stats.stat${n}.value`)}
              </p>
              <h3 className="mt-4 text-[17px] font-bold text-slate-900">
                {t(`about.stats.stat${n}.title`)}
              </h3>
              <p className="mt-2 text-[14px] leading-relaxed text-slate-600">
                {t(`about.stats.stat${n}.desc`)}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
